import { formatDistanceStrict, parseISO } from "date-fns";
import { useTheme } from "@emotion/react";
import { Skeleton } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { Chip, Typography } from "@avenue-8/sidekick-ui";
import { CopyableText } from "@features/Users/components/CopyableText";
import { StatusPill } from "@shared/components/StatusPill";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import { FormattedDate } from "@shared/components/FormattedDate/FormattedDate";
import { TablePagination } from "@shared/components/TablePagination";
import { capitalize } from "@shared/utils/text-formatter";
import type {
  ItemsStatusEnumKey,
  ListDeliveriesQueryResponse,
} from "@shared/generated/radar/models/subscriptionAuditController/ListDeliveries";
import {
  getToneColors,
  responseCodeTone,
  type StatusTone,
} from "./event-subscriptions-audit-helpers";
import {
  Card,
  CardFooter,
  CardHeader,
  CardMetaRow,
  CardsList,
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconWrap,
} from "./EventSubscriptionsAudit.styles";

type Delivery = ListDeliveriesQueryResponse["items"][number];

const DELIVERY_STATUS_TONE: Record<ItemsStatusEnumKey, StatusTone> = {
  pending: "neutral",
  succeeded: "success",
  failed: "error",
};

type Props = {
  deliveries: Delivery[];
  isLoading: boolean;
  isFiltered: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
};

export const DeliveriesAuditList = (props: Props) => {
  const {
    deliveries,
    isLoading,
    isFiltered,
    page,
    pageSize,
    total,
    onPageChange,
  } = props;
  const theme = useTheme();
  const totalPages = Math.ceil(total / pageSize);

  if (isLoading) {
    return (
      <CardsList>
        {Array(5)
          .fill(null)
          .map((_, index) => (
            <Skeleton key={index} variant="rounded" height={140} />
          ))}
      </CardsList>
    );
  }

  if (deliveries.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIconWrap>
          <HistoryOutlinedIcon />
        </EmptyStateIconWrap>
        <Typography variant="h6">
          {isFiltered ? "No matching deliveries" : "No deliveries yet"}
        </Typography>
        <EmptyStateDescription variant="body2">
          {isFiltered
            ? "No deliveries match this status filter. Try a different one."
            : "Delivery attempts will show up here once this subscription receives an event."}
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <>
      <CardsList>
        {deliveries.map((delivery) => {
          return (
            <Card key={delivery.id}>
              <CardHeader>
                <Typography variant="subtitle1">
                  {delivery.eventType}
                </Typography>
                <StatusPill
                  label={capitalize(delivery.status)}
                  {...getToneColors(
                    DELIVERY_STATUS_TONE[delivery.status],
                    theme,
                  )}
                />
              </CardHeader>

              <CardMetaRow>
                <Typography variant="body2">
                  ID
                </Typography>
                <CopyableText text={delivery.id}>{delivery.id}</CopyableText>
              </CardMetaRow>

              <CardMetaRow>
                <Typography variant="body2">
                  Resource
                </Typography>
                {delivery.eventSummary?.contactName && (
                  <Typography>{delivery.eventSummary.contactName}</Typography>
                )}
                <CopyableText text={delivery.resourceId}>
                  {delivery.resourceId}
                </CopyableText>
                {delivery.eventSummary?.type && (
                  <Chip size="small" label={delivery.eventSummary.type} />
                )}
              </CardMetaRow>

              <CardFooter>
                <CardMetaRow>
                  <Typography variant="body2">
                    {delivery.attemptCount} attempt
                    {delivery.attemptCount === 1 ? "" : "s"}
                  </Typography>
                  <BulletSeparator />
                  <Typography variant="body2">Response</Typography>
                  <StatusPill
                    label={
                      delivery.responseCode === null
                        ? "No response"
                        : String(delivery.responseCode)
                    }
                    {...getToneColors(
                      responseCodeTone(delivery.responseCode),
                      theme,
                    )}
                  />
                  <BulletSeparator />
                  <Typography variant="body2">
                    {delivery.timeTakenMs !== null
                      ? `${delivery.timeTakenMs}ms`
                      : "—"}
                  </Typography>
                  <BulletSeparator />
                  <Typography variant="body2">
                    {delivery.lastAttemptAt ? (
                      <FormattedDate
                        value={delivery.lastAttemptAt}
                        format="MMM d, yyyy h:mm:ss a"
                      />
                    ) : (
                      "not attempted yet"
                    )}
                  </Typography>
                </CardMetaRow>

                {delivery.errorMessage && (
                  <Typography variant="body2" color="error">
                    {delivery.errorMessage}
                  </Typography>
                )}

                {delivery.attemptCount > 1 && delivery.lastAttemptAt ? (
                  <Typography variant="caption" color="textSecondary">
                    {formatDistanceStrict(
                      parseISO(delivery.lastAttemptAt),
                      parseISO(delivery.createdAt),
                    )}{" "}
                    between queue and last attempt
                  </Typography>
                ) : (
                  !delivery.lastAttemptAt && (
                    <Typography variant="caption" color="textSecondary">
                      Queued{" "}
                      <FormattedDate
                        value={delivery.createdAt}
                        format="MMM d, yyyy h:mm:ss a"
                      />
                    </Typography>
                  )
                )}
              </CardFooter>
            </Card>
          );
        })}
      </CardsList>
      <TablePagination
        page={page}
        totalPages={totalPages}
        onChange={onPageChange}
      />
    </>
  );
};
